import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import couponService, { Coupon, CouponDiscountType } from '../../services/couponService';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './CouponListPage.css';
import { CirclePlus, RefreshCw, SquarePen, Trash2, TicketPercent, CircleCheck, CircleSlash } from 'lucide-react';

const CouponListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const { items } = await couponService.getAll();
      setCoupons(items);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('coupon.failedToLoad'));
    }
  }, [t]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Clear the one-shot banner from navigation state so a browser refresh does not replay it.
  useEffect(() => {
    if ((location.state as { message?: string } | null)?.message) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleDelete = async (coupon: Coupon) => {
    if (!window.confirm(t('coupon.confirmDelete', { code: coupon.code }))) return;
    try {
      await couponService.delete(coupon.id);
      setSuccessMessage(t('coupon.deleteSuccess'));
      await load();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('coupon.failedToDelete'));
    }
  };

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value));

  const formatDiscount = (coupon: Coupon) =>
    coupon.discountType === CouponDiscountType.Percentage
      ? `${coupon.discountValue}%`
      : new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2 }).format(coupon.discountValue);

  const isExpired = (coupon: Coupon) => new Date(coupon.validTo) < new Date();
  const isExhausted = (coupon: Coupon) =>
    coupon.usageLimit != null && coupon.usageCount >= coupon.usageLimit;

  const columns: GridColumn[] = [
    {
      key: 'code',
      label: t('coupon.code'),
      sortable: true,
      width: '150px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'description',
      label: t('coupon.description'),
      sortable: true,
      render: (value) => <span className="description-text">{value || '—'}</span>,
    },
    {
      key: 'discountValue',
      label: t('coupon.discount'),
      sortable: true,
      width: '120px',
      render: (_value, row) => formatDiscount(row as Coupon),
    },
    {
      key: 'validTo',
      label: t('coupon.validity'),
      sortable: true,
      width: '200px',
      render: (_value, row) => {
        const coupon = row as Coupon;
        return (
          <span className="description-text">
            {formatDate(coupon.validFrom)} – {formatDate(coupon.validTo)}
          </span>
        );
      },
    },
    {
      key: 'usageCount',
      label: t('coupon.usage'),
      sortable: true,
      width: '110px',
      render: (_value, row) => {
        const coupon = row as Coupon;
        return coupon.usageLimit != null
          ? `${coupon.usageCount} / ${coupon.usageLimit}`
          : `${coupon.usageCount} / ∞`;
      },
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '130px',
      render: (_value, row) => {
        const coupon = row as Coupon;
        if (!coupon.isActive) return <span className="ims-badge ims-badge-closed">{t('common.inactive')}</span>;
        if (isExpired(coupon)) return <span className="ims-badge ims-badge-rejected">{t('coupon.expired')}</span>;
        if (isExhausted(coupon)) return <span className="ims-badge ims-badge-rejected">{t('coupon.exhausted')}</span>;
        return <span className="ims-badge ims-badge-approved">{t('common.active')}</span>;
      },
    },
  ];

  const actions: GridAction[] = [
    ...(hasPermission(Permissions.Coupons.Edit) ? [{
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item: any) => navigate(`/sales/coupons/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    }] : []),
    ...(hasPermission(Permissions.Coupons.Delete) ? [{
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      onClick: (item: any) => handleDelete(item as Coupon),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    }] : []),
  ];

  const activeCount = coupons.filter(
    (c) => c.isActive && !isExpired(c) && !isExhausted(c)
  ).length;

  return (
    <Layout>
      <div className="container coupon-page">
        <div className="header">
          <h1 className="title">{t('coupon.title')}</h1>
          <div className="header-actions">
            {hasPermission(Permissions.Coupons.Create) && (
              <button
                className="btn-new"
                onClick={() => navigate('/sales/coupons/create')}
                title={t('coupon.newCoupon')}
              >
                <CirclePlus />
                {t('coupon.newCoupon')}
              </button>
            )}
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon coupon-icon-total"><TicketPercent size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{coupons.length}</span>
              <span className="ims-stat-label">{t('coupon.totalCoupons')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon coupon-icon-active"><CircleCheck size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">{t('coupon.currentlyUsable')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon coupon-icon-inactive"><CircleSlash size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{coupons.length - activeCount}</span>
              <span className="ims-stat-label">{t('coupon.notUsable')}</span>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={coupons}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('coupon.noCouponsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default CouponListPage;
